import React from 'react';
import { ArrowRight, Zap, Building2, Mountain, Wind, Flame, Shield } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import type { PageType } from './Header';

type ProductCategoriesProps = {
  onViewMore?: () => void;
  onNavigate?: (page: PageType) => void;
  /** When true, renders without section wrapper/header. For embedding on products page. */
  compact?: boolean;
};

const products = [
  {
    id: 'renewable-energy' as PageType,
    icon: Wind,
    title: 'Renewable Energy',
    description: 'Cutting-edge wind turbines and components designed for efficient, sustainable energy generation. These include products like rear frames, door frames, hub internals which harness wind power to provide eco-friendly solutions, reducing reliance on fossil fuels and promoting a greener future.',
    image: '/products/renewable-energy-home.jpg',
    color: 'from-teal-600 to-teal-800',
  },
  {
    id: 'steel-plant' as PageType,
    icon: Building2,
    title: 'Steel Plant',
    description: 'A Comprehensive range of equipment and lifting tools for steel production, including all types of Lances assemblies, roller tables, relining devices, segment frames, vacuum vessel, transfer cars and many more equipment were manufactured by Velmurugan Industries over 40 decades. Designed for durability and precision, these equipment enhance productivity and quality in steel manufacturing.',
    image: '/products/steel-plant-home.jpg',
    color: 'from-blue-600 to-blue-800',
  },
  {
    id: 'cement-plant' as PageType,
    icon: Mountain,
    title: 'Cement Plant',
    description: 'Velmurugan Industries has manufactured a wide range of equipment for cement plants including vertical mills. These include burner trolley, Jaw Crushers, Klin Shells, mould wall assemblies, Classifiers, HRP Frames, Klin base plates, mixers, kilns, and conveyors, cement stackers and many more engineered for high performance and efficiency, ensuring quality cement manufacturing processes.',
    image: '/products/cement-plant-home.jpg',
    color: 'from-gray-600 to-gray-800',
  },
  {
    id: 'mineral-handling' as PageType,
    icon: Zap,
    title: 'Mining and Mineral Handling',
    description: 'Specialized equipment for the extraction, processing, and transportation of minerals. These includes stacker reclaimer, feeder breaker, apron feeder crushers, conveyors, and sorting machines, bogie wheels, calciner coolers ensuring efficient and safe handling of various minerals in mining operations.',
    image: '/products/mineral-handling-home.jpg',
    color: 'from-amber-600 to-amber-800',
  },
  {
    id: 'defence' as PageType,
    icon: Shield,
    title: 'Defence',
    description: 'Critical components for defence applications including nuclear power plants. Precision manufacturing with stringent quality standards for defence and aerospace sectors.',
    image: '/products/boiler-defence-home.jpg',
    color: 'from-slate-600 to-slate-800',
  },
  {
    id: 'boiler-components' as PageType,
    icon: Flame,
    title: 'Boiler Components',
    description: 'Velmurugan Industries has an extensive portfolio of boiler components, including FGRS panels, gravimetric feeders, and boiler drums with higher wall thickness, high pressure piping, water wall panels, swage tubes. The company has also achieved significant milestones by manufacturing critical components for nuclear power plants and defence applications.',
    image: '/products/boiler-defence-home.jpg',
    color: 'from-red-600 to-red-800',
  },
];

export const ProductCategories: React.FC<ProductCategoriesProps> = ({ onViewMore, onNavigate, compact }) => {
  const { isDark } = useTheme();

  const handleProductClick = (productId: PageType) => {
    if (onNavigate) {
      onNavigate(productId);
    }
  };

  const content = (
    <>
      {!compact && (
        <>
          {/* Background Elements */}
          <div className="absolute inset-0 pointer-events-none">
            <div className={`absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 ${
              isDark ? 'bg-vmil-blue/10' : 'bg-vmil-blue/10'
            }`} />
            <div className={`absolute bottom-0 left-0 w-80 h-80 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 ${
              isDark ? 'bg-vmil-blue/10' : 'bg-vmil-blue/5'
            }`} />
          </div>

          {/* Section Header - Matches WordPress */}
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold mb-4 sm:mb-6 leading-tight ${
              isDark ? 'text-white' : 'text-vmil-blue'
            }`}>
              <span className={`underline decoration-2 sm:decoration-4 underline-offset-4 sm:underline-offset-8 ${isDark ? 'text-vmil-blue decoration-vmil-blue' : 'text-vmil-blue decoration-vmil-blue'}`}>Products</span>
            </h2>
          </div>
        </>
      )}

      {/* Products Grid */}
      <div className={`grid gap-4 sm:gap-6 md:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${compact ? 'lg:grid-cols-3' : ''}`}>
          {products.map((product) => {
            const Icon = product.icon;
            return (
                <div 
                  key={product.id}
                  onClick={() => handleProductClick(product.id)}
                  className={`group relative rounded-xl sm:rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-2 cursor-pointer ${
                    isDark 
                      ? 'bg-white/5 border border-white/10 hover:border-vmil-blue/30' 
                      : 'bg-white border border-gray-200 shadow-sm hover:shadow-xl'
                  }`}
                >
                {/* Image */}
                <div className="relative h-40 sm:h-48 overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${product.color} opacity-20`} />
                  <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6">
                  <h3 className={`text-lg sm:text-xl font-bold mb-2 sm:mb-3 transition-colors ${
                    isDark ? 'text-white group-hover:text-vmil-blue' : 'text-vmil-blue group-hover:text-vmil-blue'
                  }`}>
                    {product.title}
                  </h3>
                  <p className={`text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4 ${compact ? 'line-clamp-3' : 'line-clamp-4 sm:line-clamp-none'} ${
                    isDark ? 'text-white' : 'text-vmil-steel'
                  }`}>
                    {product.description}
                  </p>
                  <span className={`inline-flex items-center font-semibold text-xs sm:text-sm group-hover:gap-2 transition-all ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`}>
                    View Products
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1 opacity-0 group-hover:opacity-100 transition-all" />
                  </span>
                </div>

                {/* Bottom Accent */}
                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${product.color} opacity-0 group-hover:opacity-100 transition-opacity`} />
              </div>
            );
          })}
        </div>

        {/* CTA */}
        {onViewMore && (
          <div className="text-center mt-8 sm:mt-12">
            <button
              onClick={onViewMore}
              className={`inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-white font-bold text-sm sm:text-base transition-all hover:scale-105 ${
                isDark 
                  ? 'bg-vmil-blue hover:bg-vmil-blue/90 shadow-lg shadow-vmil-blue/25' 
                  : 'bg-vmil-blue hover:bg-vmil-blue/90 shadow-lg shadow-vmil-blue/25'
              }`}
            >
              View All Products
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        )}
      </>
  );

  if (compact) {
    return <>{content}</>;
  }

  return (
    <section className={`w-full py-16 md:py-24 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-vmil-navy' : 'bg-vmil-light'
    }`}>
      <div className="container mx-auto px-4 relative z-10">
        {content}
      </div>
    </section>
  );
};
